import React from "react";
import { motion } from "framer-motion";
import { shouldShowAffiliateFeatures } from "../utils/environment";

const Footer: React.FC = () => {
  const handleNavigation = (
    path: string,
    event?: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    // If modifier keys or middle-click are used, let default browser behavior handle opening in new tab
    if (
      event &&
      (event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        event.button === 1)
    ) {
      return;
    }

    if (path.startsWith("/")) {
      const currentPath = window.location.pathname.replace(/\/$/, "");
      const targetPath = path.replace(/\/$/, "");
      if (currentPath === targetPath) {
        event?.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      event?.preventDefault();
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleLinkClick = (
    path: string,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    handleNavigation(path, event);
  };

  return (
    <footer className="bg-gray-900 text-white py-8 sm:py-12 md:py-16">
      <div className="container mx-auto px-4 sm:px-5">
        {/* Glossy Fiverr Trust & Safety Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 md:p-10 mb-10 md:mb-14 overflow-hidden group shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:border-emerald-500/20 transition-all duration-300"
        >
          {/* Subtle green ambient light */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none group-hover:bg-emerald-500/10 transition-colors duration-500" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-teal-500/5 rounded-full blur-[60px] pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6 md:gap-8">
            <div className="max-w-3xl text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Official Escrow Partner
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold font-outfit text-white tracking-tight mb-3">
                Hire with Confidence: We Partner with{" "}
                <span className="text-[#1dbf73]">Fiverr</span> 💚
              </h2>
              <p className="text-gray-400 text-sm sm:text-base font-light leading-relaxed">
                We love working through <strong>Fiverr</strong> because it
                ensures ultimate safety, clarity, and peace of mind for both
                parties. Your payment is held securely in{" "}
                <strong>escrow</strong> and only released when you approve each
                milestone. It eliminates financial risk and guarantees you get
                exactly what you pay for.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 shrink-0 justify-start sm:items-center">
              <a
                href="https://www.fiverr.com/moizwordpress"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3.5 bg-[#1dbf73] hover:bg-[#10a861] text-white font-extrabold rounded-full text-sm sm:text-base transition-all duration-300 shadow-[0_4px_20px_rgba(29,191,115,0.25)] hover:scale-[1.03] active:scale-95 text-center flex items-center justify-center gap-2"
              >
                Hire Us On Fiverr <span>➔</span>
              </a>
              <a
                href="https://wa.me/923044887447?text=Hi Imran! I'd like to discuss hiring you via a Fiverr custom offer."
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3.5 bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-750 font-bold rounded-full text-sm sm:text-base transition-all duration-300 hover:scale-[1.03] text-center"
              >
                Request Custom Offer
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-8 pt-8 border-t border-white/[0.06] text-left">
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">🔒</span>
              <div>
                <h4 className="font-bold text-gray-200 text-xs sm:text-sm">
                  100% Escrow Protection
                </h4>
                <p className="text-gray-400 text-xs mt-1 font-light">
                  Funds are safely protected by Fiverr's billing system until
                  final verification.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">🤝</span>
              <div>
                <h4 className="font-bold text-gray-200 text-xs sm:text-sm">
                  Milestone Payments
                </h4>
                <p className="text-gray-400 text-xs mt-1 font-light">
                  Break down your project scope into transparent phases. Pay
                  only as work is delivered.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">🛡️</span>
              <div>
                <h4 className="font-bold text-gray-200 text-xs sm:text-sm">
                  Dispute & Quality Assurance
                </h4>
                <p className="text-gray-400 text-xs mt-1 font-light">
                  Official dispute resolution services and 24/7 support protect
                  your investments.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              MoizWP
            </h3>
            <p className="text-gray-400 leading-relaxed text-sm sm:text-base mb-4">
              Transforming businesses through innovative digital solutions and
              creative design.
            </p>

            {/* Payment Methods */}
            <div className="mb-4">
              <p className="text-gray-400 text-sm mb-2">
                We accept global payments via Payoneer
              </p>
              <p className="text-gray-400 text-xs">Customer ID: 26570613</p>
              <br />
            </div>

            {/* Social Media Links */}
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/moizwp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600 transition-colors duration-300"
                title="Facebook"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>

              <a
                href="https://x.com/moizwp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-black transition-colors duration-300"
                title="X (Twitter)"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>

              <a
                href="https://www.instagram.com/moizwp/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-500 transition-colors duration-300"
                title="Instagram"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781c-.49 0-.928-.175-1.297-.49-.368-.315-.49-.753-.49-1.243 0-.49.122-.928.49-1.243.369-.315.807-.49 1.297-.49s.928.175 1.297.49c.368.315.49.753.49 1.243 0 .49-.122.928-.49 1.243-.369.315-.807.49-1.297.49z" />
                </svg>
              </a>

              <a
                href="https://www.linkedin.com/in/imran-javed-full-stack-developer/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-700 transition-colors duration-300"
                title="LinkedIn"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </motion.div>

          {/* Industries */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              Industries
            </h3>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <a
                  href="/law-firm-website-design"
                  onClick={(e) =>
                    handleLinkClick("/law-firm-website-design", e)
                  }
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Law Firms
                </a>
              </li>
              <li>
                <a
                  href="/real-estate-ai-representation-infrastructure"
                  onClick={(e) =>
                    handleLinkClick(
                      "/real-estate-ai-representation-infrastructure",
                      e,
                    )
                  }
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Real Estate
                </a>
              </li>
              <li>
                <a
                  href="/nonprofit-website-design"
                  onClick={(e) =>
                    handleLinkClick("/nonprofit-website-design", e)
                  }
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Nonprofit Organizations
                </a>
              </li>
              <li>
                <a
                  href="/industrial-website-design"
                  onClick={(e) =>
                    handleLinkClick("/industrial-website-design", e)
                  }
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Industrial
                </a>
              </li>
              <li>
                <a
                  href="/websites-for-home-service-businesses"
                  onClick={(e) =>
                    handleLinkClick("/websites-for-home-service-businesses", e)
                  }
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Home Services
                </a>
              </li>
              <li>
                <a
                  href="/clients"
                  onClick={(e) => handleLinkClick("/clients", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Clients
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              Services
            </h3>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <a
                  href="/website-development"
                  onClick={(e) => handleLinkClick("/website-development", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Website Development
                </a>
              </li>
              <li>
                <a
                  href="/shopify-development"
                  onClick={(e) => handleLinkClick("/shopify-development", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Shopify Development
                </a>
              </li>
              <li>
                <a
                  href="/ecommerce"
                  onClick={(e) => handleLinkClick("/ecommerce", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Ecommerce Website Design
                </a>
              </li>
              <li>
                <a
                  href="/mvp-development"
                  onClick={(e) => handleLinkClick("/mvp-development", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  MVP Development
                </a>
              </li>
              <li>
                <a
                  href="/mern-stack"
                  onClick={(e) => handleLinkClick("/mern-stack", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  MERN Stack Development
                </a>
              </li>
              {shouldShowAffiliateFeatures() && (
                <>
                  <li>
                    <a
                      href="/become-affiliate"
                      onClick={(e) => handleLinkClick("/become-affiliate", e)}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center"
                    >
                      <span className="mr-2">💰</span>
                      Become an Affiliate
                    </a>
                  </li>
                  <li>
                    <a
                      href="/affiliate-portal"
                      onClick={(e) => handleLinkClick("/affiliate-portal", e)}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center"
                    >
                      <span className="mr-2">🔗</span>
                      Affiliate Portal
                    </a>
                  </li>
                </>
              )}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              Contact
            </h3>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <a
                  href="tel:+923044887447"
                  className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center"
                >
                  <svg
                    className="w-4 h-4 mr-2 text-indigo-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  +92 (304) 488-7447
                </a>
              </li>

              <li>
                <a
                  href="/create-ticket"
                  onClick={(e) => handleLinkClick("/create-ticket", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">🎫</span>
                  Create Support Ticket
                </a>
              </li>
              <li>
                <a
                  href="/ticket-lookup"
                  onClick={(e) => handleLinkClick("/ticket-lookup", e)}
                  className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">🔍</span>
                  Check Ticket Status
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-gray-800 pt-6 sm:pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-400 text-sm sm:text-base">
            &copy; 2011 - 2026 MoizWP. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-300"
            >
              XML Sitemap
            </a>
            <span className="text-gray-700 font-bold">•</span>
            <a
              href="/privacy-policy"
              onClick={(e) => handleLinkClick("/privacy-policy", e)}
              className="text-gray-400 hover:text-white transition-colors duration-300"
            >
              Privacy Policy
            </a>
            <span className="text-gray-700 font-bold">•</span>
            <a
              href="/terms-of-use"
              onClick={(e) => handleLinkClick("/terms-of-use", e)}
              className="text-gray-400 hover:text-white transition-colors duration-300"
            >
              Terms of Use
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
